import React, { useState, useContext, useEffect } from "react";
import { CSVLink } from "react-csv";
import { Box, Button, Divider, Paper, SvgIcon, Stack, Typography, LinearProgress } from '@mui/material'
import { 
    chartBarIconPath,
    downloadIconPath,
  } from '../common/iconPaths'
import ReportDateFilter from "./dateFilter";
import Charts from "./charts";
import UsageSummary from "./usageSummary";
import TouSection from "./tou/touSection";
import CustomToolTip from "../common/toolTip";
import { DataContext } from '../../contexts/data/dataContext'
import { analyticsPage, analyticsDomain, analyticsCta } from '../../services/constants'
import { sendClickAnalytics, sendViewAnalytics, getPageName } from '../common/util/util'
import custom from '../../styles/mui/szVariables.json'

export default function UsageReport({ id, title }: any) {
    const border = custom.border
    const colorSelected = "primary.main";
    const data = useContext(DataContext)
    const [stack, setStack] = useState(false);
    const [csvData, setCsvData] = useState([] as any);
    const selectedData = data?.reports?.selectedData || {}
    useEffect(()=>{
        const name = getPageName(id, 'hourly')
        sendViewAnalytics('/performance', {
                domain: analyticsDomain.trends,
                name
            })
        if(id === 'grid' && selectedData?.tou ) {
            sendViewAnalytics('/performance', {
                    domain: analyticsDomain.trends,
                    name: analyticsPage.netGridUsageBreakdown
                })
        }
    },[id])

    function handleStackClick() {
        setStack(!stack)
        sendClickAnalytics('/performance', {
              domain: analyticsDomain.trends,
              name: analyticsCta.toggleStackedView,
              metadata: {},
          })
    }

    const LinkWithRef = React.forwardRef((props: any, ref: any) => (
        <CSVLink
          ref={(c: any) => {
            ref(c?.link);
          }}
          {...props}
        />
      ));

    const csvFileName = `${data?.customer?.account?.siteId}-${selectedData.csvFileName}`
    
    return (
        <Box 
            px={custom.spacing.md}
            pt={{
                xs: custom.spacing.xxl,
                sm: data?.rightPanel?custom.spacing.md:0,
                md: 0,
                lg: 0
            }}
            >
            <Paper elevation={0} id={id+'Report'} 
                    sx={{   
                        borderRadius: custom.corners4, 
                        border,
                        width: '100%', 
                        boxSizing: 'border-box',
                    }}
                    >
                <Stack alignItems="start" direction="row" 
                        justifyContent="flex-center" 
                        pt={custom.spacing.md}
                        pl={custom.spacing.md}
                        pr={custom.spacing.sm}
                        pb={custom.spacing.xs}
                        >
                    <Typography component={"h2"} id="reportTitle"
                        variant="szHeading.Bold.Large" 
                        noWrap maxWidth={{
                            xs:"230px",
                            sm:"inherit", 
                            md:data?.rightPanel?"180px":"inherit",
                            lg:"inherit"
                        }}>
                        {title}
                    </Typography>
                    <Box ml='auto' display="flex" justifyContent={"flex-start"} mr={-2}>
                        <CustomToolTip message={stack?"Hide stacked chart":"Show stacked chart"} >
                            <SvgIcon
                                id="stackReportButton"
                                sx={{ 
                                    p: custom.spacing.xxxs, 
                                    borderRadius: custom.corners3, 
                                    border, 
                                    height: '20px',
                                    width: '24px',
                                    color: stack?"#FFF":'inherit',
                                    cursor: 'pointer',
                                    backgroundColor: stack?colorSelected:'inherit' }}
                                    onClick={handleStackClick}> 
                                    {chartBarIconPath} 
                            </SvgIcon>
                        </CustomToolTip>
                        <CustomToolTip placement={'bottom'}
                            message={`Download monthly performance csv`} >
                            <Button  
                                    id="downloadCsvReportButton"
                                    sx={{
                                        p:0,
                                        minWidth: "34px",
                                        mr: custom.spacing.sm,
                                        ml: custom.spacing.xs,
                                        cursor: 'pointer',
                                        color: "text.primary"
                                    }}
                                    component={LinkWithRef}
                                    data={csvData || []}
                                    headers={selectedData?.csvHeader || []}
                                    filename={csvFileName}
                                    target="_blank">   
                                <SvgIcon 
                                   sx={{
                                        py: custom.spacing.xxxs,
                                        pl: custom.spacing.xxs,
                                        pr: custom.spacing.xxxs,  
                                        borderRadius: custom.corners3, 
                                        border, 
                                        height: '20px',
                                        width: '20px',
                                   }}
                                   > 
                                    {downloadIconPath} 
                                </SvgIcon>
                            </Button> 
                        </CustomToolTip>
                    </Box> 
                </Stack>
                <ReportDateFilter id={id} setCsvData={setCsvData} />
                <Divider />
                {!data?.reports?.selectedData && <LinearProgress />}
                <Box display="flex" flexDirection={"row"}
                    justifyContent={"center"}>
                    <Charts id={id} stackChart={stack} />
                </Box>
                <Box pt={custom.spacing.xs}
                    px={custom.spacing.sm}
                    pb={custom.spacing.sm}
                    maxWidth="100%">
                    {
                        id === 'grid' && 
                        selectedData?.tou && 
                        selectedData?.grid?.total>0 &&
                        <TouSection />
                    }
                </Box>
            </Paper>
            <Box display="flex" 
                    flexDirection="column" 
                    justifyContent="start" 
                    alignItems="center" 
                    maxWidth="100%">
                <UsageSummary 
                    title={selectedData[id]?.reportSubTitle} 
                    description={selectedData[id]?.reportDescription} 
                    rowData={selectedData[id]?.summaryData}
                />

                {selectedData[id]?.reportSubTitle2?
                    <UsageSummary 
                        title={selectedData[id]?.reportSubTitle2} 
                        description={selectedData[id]?.reportDescription2} 
                        rowData={selectedData[id]?.summaryData2}
                    />
                :''}
            </Box> 
        </Box>      
    )
}